export interface ProviderToolSupport {
  webSearch: boolean;
  imageGeneration: boolean;
  codeExecution: boolean;
  fileSearch: boolean;
  computerUse: boolean;
  codeInterpreter: boolean;
  // xAI-specific tools
  xSearch: boolean;
  documentSearch: boolean;
  collectionsSearch: boolean;
  mcpTools: boolean;
}

export interface ProviderConfig {
  name: string;
  models: string[];
  supportsReasoning: boolean;
  supportsImages: boolean;
  supportsVideo: boolean;
  supportsAudio: boolean;
  tools: ProviderToolSupport;
}

// Provider and model configurations with tool support.
// Keep this as the single source of truth for UI capability toggles.
export const PROVIDERS = {
  openai: {
    name: 'OpenAI',
    models: [
      'gpt-5.5',
      'gpt-5.4',
      'gpt-5.4-mini',
      'gpt-5.4-nano',
      'gpt-5-mini',
      'gpt-5-nano',
      'gpt-5-pro',
      'gpt-5.1',
      'gpt-5.1-codex',
      'gpt-oss-20b',
      'gpt-oss-120b',
      'gpt-image-2',
      'gpt-image-1.5',
      'gpt-image-1-mini',
      'gpt-image-1',
    ],
    supportsReasoning: true,
    supportsImages: true,
    supportsVideo: false,
    supportsAudio: false,
    tools: {
      webSearch: true,
      imageGeneration: false, // Use gpt-image-* directly for native image gen
      codeExecution: false,
      fileSearch: true,
      computerUse: true,
      codeInterpreter: true,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  anthropic: {
    name: 'Anthropic',
    models: ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5', 'claude-opus-4-5', 'claude-sonnet-4-5'],
    supportsReasoning: true,
    supportsImages: true,
    supportsVideo: false,
    supportsAudio: false,
    tools: {
      webSearch: false,
      imageGeneration: false,
      codeExecution: false,
      fileSearch: false,
      computerUse: true,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  gemini: {
    name: 'Google Gemini',
    models: [
      'gemini-3.1-pro-preview',
      'gemini-3.1-flash',
      'gemini-3.1-flash-lite',
      'gemini-3.1-flash-image-preview',
      'gemini-2.5-flash-preview-09-2025',
      'gemini-3-pro-preview',
      'gemini-2.5-flash-lite-preview-09-2025',
      'gemini-2.0-flash-exp',
      'gemini-2.0-flash-preview-image-generation',
      'gemini-2.5-flash-image',
      'gemini-3-pro-image-preview',
      'gemini-3-flash-preview',
    ],
    supportsReasoning: true,
    supportsImages: true,
    supportsVideo: true,
    supportsAudio: true,
    tools: {
      webSearch: true,
      imageGeneration: false, // Use gemini-*-image models directly
      codeExecution: true,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  'openai-realtime': {
    name: 'OpenAI Realtime',
    // Realtime models for voice conversations via WebRTC
    models: ['gpt-realtime-2025-08-28', 'gpt-realtime-mini-2025-10-06'],
    supportsReasoning: false,
    supportsImages: false,
    supportsVideo: false,
    supportsAudio: true,
    tools: {
      webSearch: false,
      imageGeneration: false,
      codeExecution: false,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  'gemini-live': {
    name: 'Google Gemini Live',
    // Live model IDs evolve quickly; allow custom model input in UIs.
    // This list is just a sane starting point for the testing page.
    models: ['gemini-2.0-flash-exp', 'gemini-2.5-flash-native-audio-preview-12-2025'],
    supportsReasoning: false,
    supportsImages: false,
    supportsVideo: false,
    supportsAudio: true,
    tools: {
      webSearch: false,
      imageGeneration: false,
      codeExecution: false,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  xai: {
    name: 'xAI (Grok)',
    models: ['grok-4', 'grok-4.1-fast-reasoning', 'grok-4.1-fast-non-reasoning'],
    supportsReasoning: true,
    supportsImages: true,
    supportsVideo: true,
    supportsAudio: false,
    tools: {
      webSearch: true,
      imageGeneration: true,
      codeExecution: true,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: true,
      documentSearch: true,
      collectionsSearch: true,
      mcpTools: false,
    },
  },
  openrouter: {
    name: 'OpenRouter',
    models: ['openai/gpt-5', 'anthropic/claude-sonnet-4-5', 'google/gemini-3.0-pro'],
    supportsReasoning: false,
    supportsImages: true,
    supportsVideo: false,
    supportsAudio: false,
    tools: {
      webSearch: false,
      imageGeneration: false,
      codeExecution: false,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
  cerebras: {
    name: 'Cerebras',
    models: [
      'llama3.1-8b',
      'llama-3.3-70b',
      'gpt-oss-120b',
      'qwen-3-32b',
      'qwen-3-235b-a22b-instruct-2507',
      'zai-glm-4.6',
    ],
    supportsReasoning: false,
    supportsImages: false,
    supportsVideo: false,
    supportsAudio: false,
    tools: {
      webSearch: false,
      imageGeneration: false,
      codeExecution: false,
      fileSearch: false,
      computerUse: false,
      codeInterpreter: false,
      xSearch: false,
      documentSearch: false,
      collectionsSearch: false,
      mcpTools: false,
    },
  },
} as const satisfies Record<string, ProviderConfig>;

export type ProviderKey = keyof typeof PROVIDERS;

export interface ToolConfig {
  webSearch: boolean;
  imageGeneration: boolean;
  codeExecution: boolean;
  fileSearch: boolean;
  computerUse: boolean;
  codeInterpreter: boolean;
  // xAI-specific tools
  xSearch: boolean;
  documentSearch: boolean;
  collectionsSearch: boolean;
  mcpTools: boolean;
}

export function isNativeImageModel(model: string): boolean {
  return (
    model === 'gpt-image-1' ||
    model === 'gpt-image-2' ||
    model.includes('-image') ||
    model === 'gemini-2.0-flash-exp' ||
    model === 'gemini-2.0-flash-preview-image-generation' ||
    model === 'gemini-2.5-flash-image' ||
    model === 'gemini-3.1-flash-image-preview' ||
    model === 'gemini-3-pro-image-preview'
  );
}

/**
 * Check if a provider is a realtime voice provider
 */
export function isRealtimeProvider(provider: string): provider is 'openai-realtime' | 'gemini-live' {
  return provider === 'openai-realtime' || provider === 'gemini-live';
}

/**
 * Get the list of available realtime providers
 */
export function getRealtimeProviders(): Array<{ id: string; name: string; models: readonly string[] }> {
  return [
    { id: 'openai-realtime', name: PROVIDERS['openai-realtime'].name, models: PROVIDERS['openai-realtime'].models },
    { id: 'gemini-live', name: PROVIDERS['gemini-live'].name, models: PROVIDERS['gemini-live'].models },
  ];
}
